import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../../services/baseQuery';
import { gridApi } from '../../grid';
import { summaryApi } from '../../summary';

const SENSITIVE_DATA_API = '/umbraco/api/SensitiveData';
const TAG_STATUS = 'SensitiveDataStatus';

export type SensitiveDataStatus = {
  isSet: boolean;
};

type SetPasswordRequest = {
  Password: string;
};

/**
 * RTK Query API slice for the sensitive data token endpoints.
 *
 * The token is stored as an HttpOnly cookie by the backend; this slice manages:
 *  - Querying whether the cookie is currently set (GetStatus)
 *  - Setting the password cookie (SetPassword)
 *  - Clearing the password cookie (ClearPassword)
 */
export const sensitiveDataApi = createApi({
  reducerPath: 'sensitiveDataApi',
  baseQuery,
  tagTypes: [TAG_STATUS],
  endpoints: (builder) => ({
    getPasswordStatus: builder.query<SensitiveDataStatus, void>({
      query: () => ({
        url: `${SENSITIVE_DATA_API}/GetPasswordStatus`,
        method: 'GET',
      }),
      transformResponse: (response: { Code: number; data: SensitiveDataStatus }) => response.data,
      providesTags: [TAG_STATUS],
    }),

    setPassword: builder.mutation<void, SetPasswordRequest>({
      query: (body) => ({
        url: `${SENSITIVE_DATA_API}/SetPassword`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [TAG_STATUS],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(gridApi.util.invalidateTags(['GridData', 'Entity']));
          dispatch(summaryApi.util.invalidateTags(['Summary']));
        } catch {
          console.error('Failed to set sensitive data password');
        }
      },
    }),

    clearPassword: builder.mutation<void, void>({
      query: () => ({
        url: `${SENSITIVE_DATA_API}/ClearPassword`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: [TAG_STATUS],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(gridApi.util.invalidateTags(['GridData', 'Entity']));
          dispatch(summaryApi.util.invalidateTags(['Summary']));
        } catch {
          console.error('Failed to clear sensitive data password');
        }
      },
    }),
  }),
});

export const { useGetPasswordStatusQuery, useSetPasswordMutation, useClearPasswordMutation } =
  sensitiveDataApi;
